import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AccountService } from '@wilson/account';
import { FeaturePurePipe } from '@wilson/authorization';
import { FeatureName, UserOvertime } from '@wilson/interfaces';
import { WilsonState } from '@wilson/non-domain-specific/decorators/wilson-state';
import { formatISO, startOfYear } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  catchError,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

@Injectable()
export class FindStaffDataService {
  private featurePurePipe = inject(FeaturePurePipe);
  private accountService = inject(AccountService);
  private nzMessageService = inject(NzMessageService);
  private translateService = inject(TranslateService);

  private unSubFetchUsersOvertimesRequestSubject = new Subject();

  @WilsonState<UserOvertime[]>([])
  usersOvertimes!: UserOvertime[];
  usersOvertimes$!: Observable<UserOvertime[]>;

  @WilsonState<boolean>(false)
  isLoadingUsersOvertimes!: boolean;
  isLoadingUsersOvertimes$!: Observable<boolean>;

  public timelineFetchUsersOvertimes(): Observable<UserOvertime[] | null> {
    const allowOvertime$ = this.featurePurePipe.transform(FeatureName.Payroll);

    return allowOvertime$.pipe(
      switchMap((allowOvertime) => {
        if (allowOvertime) {
          this.unSubFetchUsersOvertimesRequestSubject.next(null);
          this.isLoadingUsersOvertimes = true;

          return this.accountService
            .getUserOvertimes([
              {
                start: formatISO(startOfYear(new Date())),
                end: formatISO(new Date()),
              },
            ])
            .pipe(
              takeUntil(this.unSubFetchUsersOvertimesRequestSubject),
              tap((usersOvertimes: UserOvertime[]) => {
                this.usersOvertimes = usersOvertimes;
                this.isLoadingUsersOvertimes = false;
              }),
              catchError(() => {
                this.nzMessageService.error(
                  this.translateService.instant('general.something_went_wrong'),
                );
                return of();
              }),
            );
        } else {
          return of(null);
        }
      }),
    );
  }

  public userOvertimeMinutesStream(
    userId: string,
  ): Observable<number | undefined> {
    return this.usersOvertimes$.pipe(
      map((usersOvertimes) => {
        return usersOvertimes.find(
          (userOvertimes) => userOvertimes.user.id === userId,
        )?.overtimes[0].overtimeInMinutes;
      }),
    );
  }
}
